import { geoDistance, geoInterpolate } from "d3-geo";

/**
 * The globe's geometry, shared by the Trade Feed globe and the Arbitrages
 * globe (2026-10-12). Moved here verbatim from TradeFeedGlobe.jsx — the reasons
 * behind each choice are recorded there and still hold.
 */

/** How much of the sphere is in view at 1x — just under a full hemisphere. */
export const BASE_CAP_DEGREES = 87;

/**
 * General perspective ("satellite") projection: a camera at a finite distance,
 * which is what makes zooming read as three-dimensional. `P` is the camera's
 * distance in Earth radii; P → ∞ is orthographic.
 */
export const satelliteRaw = (P) => {
  const forward = (lambda, phi) => {
    const cosPhi = Math.cos(phi);
    const k = (P - 1) / (P - cosPhi * Math.cos(lambda));
    return [k * cosPhi * Math.sin(lambda), k * Math.sin(phi)];
  };

  forward.invert = (x, y) => {
    const rho = Math.sqrt(x * x + y * y);
    const sinc =
      (P - Math.sqrt(1 - (rho * rho * (P + 1)) / (P - 1))) / ((P - 1) / rho + rho / (P - 1));
    return [
      Math.atan2(x * sinc, rho * Math.sqrt(1 - sinc * sinc)),
      rho ? Math.asin((y * sinc) / rho) : 0,
    ];
  };

  return forward;
};

/** Camera distance and on-screen scale for a zoom level; the disc keeps its size. */
export const cameraFor = (zoom, disc) => {
  const cap = ((BASE_CAP_DEGREES / zoom) * Math.PI) / 180;
  const P = 1 / Math.cos(cap);

  return { P, cap, scale: disc / Math.sqrt((P - 1) / (P + 1)) };
};

/** Great-circle line between two [lon, lat] points, as something geoPath can draw. */
export const arcBetween = (from, to) => {
  const interpolate = geoInterpolate(from, to);
  const coordinates = Array.from({ length: 49 }, (_, i) => interpolate(i / 48));
  return { type: "LineString", coordinates };
};

/** Is this point inside the visible cap? The projection does NOT clip points. */
export const isFacing = ([lon, lat], rotation, cap) =>
  geoDistance([lon, lat], [-rotation[0], -rotation[1]]) < cap;

/**
 * The base map, fetched once per session and shared.
 *
 * A dynamic import keeps the atlas out of every page that has no globe. The
 * promise is memoised so a page can start it early (the Arbitrages grid does,
 * on mount) and the popup then opens onto a map that is already there.
 */
let worldPromise = null;

export const loadWorld = () => {
  if (!worldPromise) {
    worldPromise = Promise.all([import("world-atlas/countries-110m.json"), import("topojson-client")])
      .then(([atlas, topojson]) => {
        const world = atlas.default ?? atlas;
        return topojson.feature(world, world.objects.countries);
      })
      .catch((error) => {
        // Let a later call retry rather than caching the failure forever.
        worldPromise = null;
        throw error;
      });
  }
  return worldPromise;
};
